<template>
  <div :class="mainClass" ref="simpleSelect">
    <div
      :class="[
        headerClass,
        disabled ? `${headerClass}__disabled` : '',
        hasError ? 'has-error' : '',
        open ? `${headerClass}__open` : `${headerClass}`
      ]"
    >
      <input
        :id="inputId"
        :class="[disabled ? `${inputClass}__disabled` : `${inputClass}`]"
        :placeholder="selectedLabel"
        v-model="search"
        :disabled="disabled"
        @focus="openList()"
        @blur="closeList()"
        :tabindex="tabindex"
        @keyup="change"
        ref="search"
        readonly="true"
      />
      <span
        v-if="!spinner"
        :class="[open ? `${caretClass}__open` : `${caretClass}`]"
        @mousedown.prevent="toggleOpen()"
      >
        <slot name="caret">
          <i class="fa fa-caret-down"></i>
        </slot>
      </span>
      <span v-else :class="`${caretClass}__spinner`">
        <slot name="spinner">
          <i class="fa fa-spinner fa-spin"></i>
        </slot>
      </span>
    </div>
    <div :class="listClass" v-show="open" ref="options">
      <ul v-show="open" @mousemove="resetPointerEvents">
        <template v-for="(option, index) in data">
          <li
            @mousedown.prevent="update(option)"
            :key="index"
            @mouseover="updatePosition(index)"
            :class="[
              isActive(option) ? 'select-option__active' : '',
              atPosition(index) ? 'select-option__hover' : '',
              itemClass
            ]"
            ref="option"
          >
            <slot name="option" :option="option">
              <template v-if="typeof option === 'object'">
                <span :class="labelClass">{{ option[label] }}</span>
              </template>
              <template v-else>
                <span :class="labelClass">{{ option }}</span>
              </template>
            </slot>
            <template v-if="remove">
              <span
                class="remove-option pull-right"
                @mousedown.prevent.stop="$emit('remove', option)"
              >
                <slot name="removeIcon">
                  <i class="fa fa-trash"></i>
                </slot>
              </span>
            </template>
            <span v-if="optionSelected(option)" class="selection"
              >Deselect</span
            >
          </li>
        </template>
        <li v-if="data.length === 0" :class="itemClass">No results...</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "SimpleSelect",
  props: {
    value: {},
    nullValue: { default: "" },
    // TODO multiselect
    multiSelect: { type: Boolean, default: false },
    remove: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    spinner: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select" },
    options: { type: Array, default: () => [] },
    label: { type: String, default: "name" },
    returnValue: { type: String, default: "id" },
    mainClass: { default: "simple-select" },
    headerClass: { default: "simple-select-header" },
    inputClass: { default: "simple-select-input" },
    inputId: { default: "" },
    caretClass: { type: String, default: "simple-select-caret" },
    listClass: { type: String, default: "simple-select-options" },
    itemClass: { type: String, default: "select-option" },
    labelClass: { type: String, default: "select-label" },
    tabindex: { type: String }
  },
  data: () => ({
    open: false,
    selected: "",
    position: 0,
    search: ""
  }),
  computed: {
    selectedLabel() {
      return this.selected && this.options.indexOf(this.selected) !== -1
        ? typeof this.selected === "object"
          ? this.selected[this.label]
          : this.selected
        : this.placeholder;
    },
    data() {
      let searchCallback = option => {
        return typeof option === "object"
          ? option[this.label]
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase())
          : option
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase());
      };
      if (this.selected) {
        let data = [];
        data = data.concat(
          this.options.filter(option => option === this.selected)
        );
        data = data.concat(
          this.options.filter(option => option !== this.selected)
        );
        return data.filter(searchCallback);
      }
      return this.options.filter(searchCallback);
    }
  },
  methods: {
    toggleOpen() {
      if (!this.disabled) {
        this.open ? this.closeList() : this.openList();
      }
    },
    openList() {
      this.resetPosition();
      this.open = true;
      this.$refs.search.focus();
      window.addEventListener("keydown", this.keydown, false);
      this.$emit("search", this.search);
    },
    closeList() {
      this.$nextTick(() => {
        this.open = false;
        if (this.searchable) {
          if (this.value === null) {
            this.search = "";
          } else {
            this.search = this.value.toString();
          }
        } else {
          this.search = "";
        }
        this.$refs.search.blur();
        window.removeEventListener("keydown", this.keydown, false);
        this.$emit("search", this.search);
        this.$emit("blur");
      });
    },
    update(option) {
      this.multiSelect ? this.updateMulti(option) : this.updateSingle(option);
    },
    updateMulti() {
      // TODO multiselect
      // this.selected.push(option);
    },
    updateSingle(option) {
      this.selected = this.optionSelected(option) ? this.nullValue : option;
      if (this.selected === this.nullValue) {
        this.$emit("input", this.nullValue);
      } else {
        this.$emit(
          "input",
          this.returnValue.length
            ? this.selected[this.returnValue]
            : this.selected
        );
      }
      this.closeList();
    },
    isActive(option) {
      return this.optionSelected(option);
    },
    setSelected(val) {
      if (this.returnValue.length && val !== null) {
        this.selected = this.options.find(
          option => option[this.returnValue] === val
        );
      } else {
        this.selected = val;
      }
    },
    optionSelected(option) {
      if (typeof option === "object") {
        return JSON.stringify(option) === JSON.stringify(this.selected);
      } else {
        return option === this.selected;
      }
    },
    atPosition(index) {
      return this.position === index;
    },
    updatePosition(index) {
      this.position = index;
    },
    resetPosition() {
      this.position = 0;
    },
    getSelectedId() {
      return this.selected ? this.selected.id : "";
    },
    getSelectedRefId() {
      return this.selected ? this.selected.ref_id : "";
    },
    setSelectedId(val) {
      if (this.returnValue.length && val !== null) {
        this.selected = this.options.find(
          option => option[this.returnValue] === val
        );
        this.search = this.selected[this.returnValue];
      } else {
        this.selected = val;
      }
    },
    disablePointerEvents() {
      this.data.forEach((o, index) => {
        this.$refs.option[index].style.pointerEvents = "none";
      });
    },
    resetPointerEvents() {
      this.data.forEach((o, index) => {
        this.$refs.option[index].style.pointerEvents = "auto";
      });
    },
    keydown(e) {
      const key = e.key || e.keyCode || e.which || e.charCode;
      if (key === "ArrowUp") {
        e.preventDefault();
        if (this.position > 0) --this.position;
        this.disablePointerEvents();
        if (
          this.$refs.option[0].clientHeight * this.position <
          this.$refs.options.scrollTop
        ) {
          this.$refs.options.scrollTop =
            this.$refs.option[0].clientHeight * this.position;
        }
      }
      if (key === "ArrowDown") {
        e.preventDefault();
        if (this.position < this.data.length - 1) ++this.position;
        this.disablePointerEvents();
        if (this.$refs.option[0].clientHeight * (this.position + 1) > 300) {
          this.$refs.options.scrollTop =
            this.$refs.option[0].clientHeight * (this.position + 1) - 300;
        }
      }
      if (key === "Enter") {
        e.preventDefault();
        this.update(this.data[this.position]);
      }
    },
    change(e) {
      const key = e.key || e.keyCode || e.which || e.charCode;
      if (
        this.searchable &&
        key !== "Enter" &&
        key !== "ArrowDown" &&
        key !== "ArrowUp"
      ) {
        this.$emit("search", this.search);
      }
    }
  },
  watch: {
    value(val) {
      this.setSelected(val);
      if (this.searchable) {
        this.search = this.value.toString();
      }
    },
    options() {
      if (this.value) {
        this.setSelected(this.value);
      }
    }
  },
  created() {
    this.selected = this.nullValue;
    if (this.value) {
      this.setSelected(this.value);
      if (this.searchable) {
        this.search = this.value.toString();
      }
      this.$emit("search", this.search);
    }
  }
};
</script>
<style lang="scss">
/*
  Define here the CSS styles applied only to Safari browsers
  (any version and any device)
 */
@supports (-webkit-backdrop-filter: blur(1px)) {
  .simple-select-caret,
  .simple-select-caret__spinner,
  .simple-select-caret__open {
    right: 0;
  }
}

.simple-select {
  position: relative;
  cursor: pointer;
  font-size: 14px;
  width: calc(100% - 15px);
}

.simple-select-header {
  background-color: white;
}
.simple-select-header__disabled {
  background-color: #f3f3f4;
}
.simple-select-header.has-error {
  border-color: #ff5722;
}

.simple-select-input {
  border: none;
  width: calc(100% - 25px);
}

.simple-select-input__disabled {
  border: none;
  background-color: #f3f3f4;
  padding-left: 15px;
  line-height: 30px;
  width: calc(100% - 25px);
}
.simple-select-input:focus {
  outline: none;
}

.simple-select-caret {
  transition: 0.2s ease-in-out;
  display: inline-block;
  padding: 0 6px;
  right: -5px;
  position: absolute;
  top: -5px;
}
.simple-select-caret__spinner {
  transition: 0.2s ease-in-out;
  color: #00cc55;
  display: inline-block;
  padding: 0 6px;
  position: absolute;
  top: -5px;
}

.simple-select-caret__open {
  transform: rotate(180deg);
  transition: 0.2s ease-in-out;
  display: inline-block;
  padding: 0 6px;
  position: absolute;
  top: -5px;
}

.simple-select {
  position: relative;
  cursor: pointer;
  font-size: 14px;
}

.simple-select-header-white {
  border-radius: 4px;
  background-color: white;
}
.simple-select-header__disabled {
  background-color: #f3f3f4;
}
.simple-select-header.has-error {
  border-color: #ff5722;
}

.simple-select-input {
  border: none;
  width: calc(100% - 25px);
  cursor: pointer;
}

.simple-select-input-white {
  border: none;
  padding-left: 12px;
  padding-right: 12px;
  line-height: 40px;
  width: calc(100% - 25px);
  border-radius: 4px;
  font-size: 10px;
  background-color: white;
}

.simple-select-input__disabled {
  border: none;
  background-color: #f3f3f4;
  padding-left: 15px;
  padding-right: 15px;
  line-height: 40px;
  width: calc(100% - 25px);
  font-size: 10px;
}
.simple-select-input:focus {
  outline: none;
}

.simple-select-options {
  position: absolute;
  background-color: white;
  border-radius: 2px;
  max-height: 300px;
  overflow-y: auto;
  cursor: pointer;
  z-index: 5;
  font-size: 12px;
  white-space: nowrap;
  min-width: calc(100% + 2px);
  top: 50px;
}

.simple-select-options ul {
  list-style: none;
  padding: 0;
  margin-bottom: 0;
}
.select-header {
  background-color: white;
}
.select-header__disabled {
  background-color: #f3f3f4;
}
.select-input {
  border: none;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 15px;
  line-height: 38px;
  width: calc(100% - 25px);
}
.select-input__disabled {
  border: none;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 15px;
  line-height: 38px;
  width: calc(100% - 25px);
  background-color: #f3f3f4;
}

.select-input:focus {
  outline: none;
}

.select-caret {
  transition: 0.2s ease-in-out;
  display: inline-block;
  line-height: 38px;
  padding: 0 6px;
  position: absolute;
  top: 1px;
  right: 0;
}
.select-caret__spinner {
  transition: 0.2s ease-in-out;
  color: #00bfa5;
  display: inline-block;
  line-height: 38px;
  padding: 0 6px;
  position: absolute;
  top: 1px;
  right: 0;
}

.select-caret__open {
  transform: rotate(180deg);
  transition: 0.2s ease-in-out;
  display: inline-block;
  line-height: 38px;
  padding: 0 6px;
  position: absolute;
  top: 1px;
  right: 0;
}

.select-options {
  position: absolute;
  border: 1px solid #e5e6e7;
  top: 40px;
  left: 0;
  right: 0;
  background-color: white;
  max-height: 300px;
  overflow-y: auto;
  cursor: pointer;
  z-index: 5;
}

.select-options ul {
  list-style: none;
  padding: 0;
  margin-bottom: 0;
}
.select-option {
  position: relative;
  padding: 5px 10px;
  margin: 2px;
  font-weight: 100;
  border-radius: 2px;
}

.select-option .remove-option {
  position: absolute;
  top: 5px;
  right: 10px;
}

.select-option__hover {
  background-color: #eeeeee;
}

.select-option__active {
  font-weight: bold;
  background-color: whitesmoke;
}

.select-option__active.select-option__hover {
  background-color: #ff2929;
}

.select-option .selection {
  display: none;
}

.select-option__active.select-option__hover:hover .selection {
  display: inline;
  font-size: 10px;
}

.select-option__active.select-option__hover:hover .select-label {
  display: none;
}

/* Homepage select*/

.hero-section .select-option,
.book-section .select-option {
  background-color: transparent;
  color: #000;
}

.hero-section .simple-select-header,
.book-section .simple-select-header {
  background-color: transparent;
}

.hero-section .simple-select-input,
.book-section .simple-select-input {
  background-color: transparent;
  height: 34px;
  padding-left: 0;
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-size: 1rem;
  }
  &::-ms-input-placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-size: 1rem;
  }
  &:-ms-input-placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-size: 1rem;
  }
}

/* Filter wrap */

.filter-wrap .form-control {
  padding: 0;
  border: none;
}
/* Select */

.select-wrap label {
  padding-left: 16px;
  margin-bottom: 0;
}

.select-wrap p {
  padding: 0 0 0 16px;
  font-size: 16px;
  margin: 0;
}

.simple-select-input,
.select-wrap .flatpickr-input,
.filters .flatpickr-input,
.simple-select-input__disabled {
  padding: 0 0 0 16px;
  font-size: 16px;
  color: #000;
  border: 0;
  background-color: transparent;
}

.select-option {
  background-color: #fff;
  margin: 0;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.71;
  color: #4c5669;
  border-radius: 0;
  border-top: solid 1px transparent;
  border-left: solid 1px transparent;
  border-right: solid 1px transparent;
  border-bottom: solid 1px #ececec;
}

.select-option-boolean:hover {
  border: solid 1px rgba(41, 98, 255, 0.5);
  background-color: rgba(41, 98, 255, 0.05);
}

.select-option .selection,
.select-option__active.select-option__hover:hover .selection {
  text-transform: capitalize;
  font-size: 14px;
}

.select-option__active.select-option__hover,
.select-option__hover,
.select-option__active.select-wrap {
  border: solid 1px rgba(41, 98, 255, 0.5);
  background-color: rgba(41, 98, 255, 0.05);
}

.select-wrap .form-control:disabled,
.simple-select-input__disabled,
.simple-select-input:disabled {
  background-color: transparent;
  opacity: 0.5;
}

.hero-section .simple-select-input::placeholder {
  color: rgba(97, 97, 97, 0.4) !important;
}
.find-rates-page .simple-select-caret,
.find-rates-page .simple-select-caret__open {
  display: none !important;
}
</style>
